import { Component, ChangeDetectorRef } from '@angular/core';
import { UploadStateService } from '../services/upload-state.service';
import { UploadApiService } from '../services/upload-api.service';

interface UploadedFile {
  name: string;
  size: number;
  file: File;
  status: 'pending' | 'success' | 'error';
  api?: string;
  csvHeaders?: string[];
  columnMapping?: ColumnMapping;
  needsMapping?: boolean;
  detectedEpsgCode?: number | null;
  selectedEpsgCode?: number | null;
  epsgRegion?: string | null;
  epsgDescription?: string | null;
  epsgError?: string | null;
  azimuthReference?: 'true_north' | 'grid_north'; // Azimuth reference system
}

interface ColumnMapping {
  MD: string;
  INC: string;
  AZI: string;
}

@Component({
  selector: 'app-upload',
  standalone: false,
  templateUrl: './upload.html',
  styleUrl: './upload.scss',
})
export class Upload {
  isDragging = false;
  requiredColumns = ['MD', 'INC', 'AZI'];
  currentMappingFile: UploadedFile | null = null;
  showMappingDialog = false;
  showResultsDialog = false;
  showEpsgDialog = false;
  isUploading = false;
  epsgDialogShownOnce = false; // Track if EPSG dialog was already shown
  apiSortDirection: 'asc' | 'desc' | null = null; // Track API column sort direction
  azimuthReference: 'true_north' | 'grid_north' = 'true_north'; // Default to True North
  epsgCodes = [
    { code: 32065, name: 'NAD27 / Louisiana South (EPSG:32065)' },
    { code: 32012, name: 'NAD27 / New Mexico East (EPSG:32012)' },
    { code: 32013, name: 'NAD27 / New Mexico Central (EPSG:32013)' },
    { code: 32014, name: 'NAD27 / New Mexico West (EPSG:32014)' },

    { code: 32037, name: 'NAD27 / Texas North (EPSG:32037)' },
    { code: 32038, name: 'NAD27 / Texas North Central (EPSG:32038)' },
    { code: 32039, name: 'NAD27 / Texas Central (EPSG:32039)' },
    { code: 32040, name: 'NAD27 / Texas South Central (EPSG:32040)' },
    { code: 32041, name: 'NAD27 / Texas South (EPSG:32041)' }
  ];

  // Matches API number patterns like 42-123-45678 or 4212345678 (with or without dashes)
  private readonly apiNumberPattern = /\d{2}-?\d{3}-?\d{5}/;
  private readonly api12DigitPattern = /\d{12}/;

  constructor(
    private cdr: ChangeDetectorRef,
    private uploadApi: UploadApiService,
    public state: UploadStateService
  ) {}

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    this.handleFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  private async handleFiles(files: FileList): Promise<void> {
    const rejectedFiles: string[] = [];

    for (const file of Array.from(files)) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        if (!this.apiNumberPattern.test(file.name)) {
          rejectedFiles.push(file.name);
          continue;
        }

        const headers = await this.readCSVHeaders(file);
        const missingColumns = this.checkRequiredColumns(headers);
        
        const uploadedFile: UploadedFile = {
          name: file.name,
          size: file.size,
          file: file,
          status: missingColumns.length > 0 ? 'pending' : 'success',
          api: this.extractApiFromFilename(file.name) || undefined,
          csvHeaders: headers,
          needsMapping: missingColumns.length > 0,
          columnMapping: this.createDefaultMapping(headers),
          detectedEpsgCode: null,
          selectedEpsgCode: null,
          epsgRegion: null,
          epsgDescription: null,
          epsgError: null,
          azimuthReference: this.azimuthReference, // Set default azimuth reference
        };
        
        this.state.uploadedFiles.push(uploadedFile);
        
        // If columns are missing, open mapping dialog for this file
        if (missingColumns.length > 0) {
          this.openMappingDialog(uploadedFile);
        }
      } else {
        alert(`${file.name} is not a CSV file`);
      }
    }

    if (rejectedFiles.length > 0) {
      alert(
        `The following file(s) were rejected because they do not contain an API number in the filename:\n\n` +
        rejectedFiles.join('\n')
      );
    }

    // Trigger change detection after all files are processed
    this.cdr.detectChanges();

    // Auto-open EPSG confirmation when all files are ready (no mapping pending) - only once
    if (this.areAllFilesReadyForValidation() && !this.epsgDialogShownOnce) {
      this.epsgDialogShownOnce = true;
      this.resolveEpsgAndOpenDialog();
    }
  }

  private readCSVHeaders(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const firstLine = text.split('\n')[0];
        const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
        resolve(headers);
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  private checkRequiredColumns(headers: string[]): string[] {
    const headersUpper = headers.map(h => h.toUpperCase());
    return this.requiredColumns.filter(
      col => !headersUpper.includes(col.toUpperCase())
    );
  }

  private createDefaultMapping(headers: string[]): ColumnMapping {
    const mapping: any = {};
    const headersUpper = headers.map(h => h.toUpperCase());
    
    this.requiredColumns.forEach(reqCol => {
      const index = headersUpper.indexOf(reqCol.toUpperCase());
      mapping[reqCol] = index !== -1 ? headers[index] : '';
    });
    
    return mapping as ColumnMapping;
  }

  removeFile(index: number): void {
    this.state.uploadedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  openMappingDialog(file: UploadedFile): void {
    this.currentMappingFile = file;
    this.showMappingDialog = true;
  }

  closeMappingDialog(): void {
    this.showMappingDialog = false;
    this.currentMappingFile = null;
  }

  getColumnMapping(column: string): string {
    if (!this.currentMappingFile?.columnMapping) return '';
    return this.currentMappingFile.columnMapping[column as keyof ColumnMapping] || '';
  }

  setColumnMapping(column: string, value: string): void {
    if (!this.currentMappingFile?.columnMapping) return;
    (this.currentMappingFile.columnMapping as any)[column] = value;
  }

  isHeaderUsed(header: string, currentCol: string): boolean {
    if (!this.currentMappingFile?.columnMapping) return false;
    const mapping = this.currentMappingFile.columnMapping;
    return this.requiredColumns.some(
      col => col !== currentCol && mapping[col as keyof ColumnMapping] === header
    );
  }

  saveMappingAndClose(): void {
    if (this.currentMappingFile && this.currentMappingFile.columnMapping) {
      const mapping = this.currentMappingFile.columnMapping;
      
      // Validate that all required columns are mapped
      const unmappedColumns: string[] = [];
      
      if (!mapping.MD || mapping.MD.trim() === '') unmappedColumns.push('MD');
      if (!mapping.INC || mapping.INC.trim() === '') unmappedColumns.push('INC');
      if (!mapping.AZI || mapping.AZI.trim() === '') unmappedColumns.push('AZI');
      
      if (unmappedColumns.length > 0) {
        alert(`Please map all required columns: ${unmappedColumns.join(', ')}`);
        return;
      }
      
      this.currentMappingFile.needsMapping = false;
      this.currentMappingFile.status = 'success';
    }
    
    this.closeMappingDialog();

    // If mapping for all files is now complete, show EPSG confirmation automatically - only once
    if (this.areAllFilesReadyForValidation() && !this.epsgDialogShownOnce) {
      this.epsgDialogShownOnce = true;
      this.resolveEpsgAndOpenDialog();
    }
  }

  uploadFiles(): void {
    if (this.state.uploadedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    if (this.state.toleranceThreshold === null || this.state.toleranceThreshold < 0 || this.state.toleranceThreshold > 10000) {
      alert('Please enter a valid tolerance threshold (must be between 0 and 10000)');
      return;
    }
    
    // Check if any files still need mapping
    const unmappedFiles = this.state.uploadedFiles.filter(f => f.needsMapping);
    if (unmappedFiles.length > 0) {
      alert(`Please map columns for: ${unmappedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    // Check if all files already have EPSG codes selected
    const allHaveEpsg = this.state.uploadedFiles.every(
      f => f.selectedEpsgCode !== null && f.selectedEpsgCode !== undefined && !Number.isNaN(Number(f.selectedEpsgCode))
    );

    if (allHaveEpsg) {
      // If EPSG codes are already set, proceed directly to upload
      this.confirmEpsgAndUpload();
    } else {
      // If EPSG codes are missing, open the dialog
      this.resolveEpsgAndOpenDialog();
    }
  }

  closeEpsgDialog(): void {
    this.showEpsgDialog = false;
  }

  // Method to manually reopen EPSG dialog for review/editing
  openEpsgDialogManually(): void {
    if (this.state.uploadedFiles.length === 0) {
      alert('Please select files first');
      return;
    }
    this.resolveEpsgAndOpenDialog();
  }

  confirmEpsgCodes(): void {
    // Validate that all files have valid EPSG codes
    const invalidFiles = this.state.uploadedFiles.filter(
      f => f.selectedEpsgCode === null || f.selectedEpsgCode === undefined || Number.isNaN(Number(f.selectedEpsgCode))
    );

    if (invalidFiles.length > 0) {
      alert(`Please provide a valid EPSG code for: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Just close the dialog - EPSG codes are already set on the files
    this.showEpsgDialog = false;
  }

  confirmEpsgAndUpload(): void {
    const invalidFiles = this.state.uploadedFiles.filter(
      f => f.selectedEpsgCode === null || f.selectedEpsgCode === undefined || Number.isNaN(Number(f.selectedEpsgCode))
    );

    if (invalidFiles.length > 0) {
      alert(`Please provide a valid EPSG code for: ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    this.showEpsgDialog = false;

    const toleranceThreshold = this.state.toleranceThreshold;
    if (toleranceThreshold === null) {
      alert('Please enter a valid tolerance threshold.');
      return;
    }

    // Create FormData for multipart/form-data upload
    const formData = new FormData();
    
    // Add tolerance threshold
    formData.append('tolerance_threshold', toleranceThreshold.toString());
    
    // Add each CSV file with its column mapping
    this.state.uploadedFiles.forEach((uploadedFile, index) => {
      // Add the actual file
      formData.append('files', uploadedFile.file, uploadedFile.file.name);
      
      // Add column mapping as JSON string
      formData.append(`mapping_${index}`, JSON.stringify({
        filename: uploadedFile.file.name,
        columns: uploadedFile.columnMapping
      }));

      formData.append(`epsg_override_${index}`, JSON.stringify({
        filename: uploadedFile.file.name,
        epsg_code: Number(uploadedFile.selectedEpsgCode)
      }));

      // Add azimuth reference configuration
      formData.append(`azimuth_ref_${index}`, JSON.stringify({
        filename: uploadedFile.file.name,
        azimuth_reference: uploadedFile.azimuthReference || 'true_north'
      }));
    });

    this.isUploading = true;
    this.state.uploadError = null;
    this.state.uploadResults = [];

    this.uploadApi.upload(formData).subscribe({
      next: (response) => {
        this.state.uploadResults = response.results || [];
        this.isUploading = false;
        this.showResultsDialog = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        const message = err?.error?.detail || err?.message || 'Upload failed';
        this.state.uploadError = message;
        this.isUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  refreshRegionForEditedEpsg(file: UploadedFile): void {
    const value = Number(file.selectedEpsgCode);
    if (!Number.isInteger(value) || value <= 0) {
      file.epsgRegion = null;
      file.epsgDescription = null;
      file.epsgError = 'Please enter a valid EPSG code';
      return;
    }

    file.epsgError = 'Checking region...';
    this.uploadApi.resolveEpsgCode(value).subscribe({
      next: (response) => {
        file.epsgRegion = response.region ?? null;
        file.epsgDescription = response.region_description ?? null;

        if (response.error) {
          file.epsgError = response.error;
        } else if (!response.region) {
          file.epsgError = 'Region not found';
        } else {
          file.epsgError = null;
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        const message = err?.error?.detail || err?.message || 'Failed to validate EPSG';
        file.epsgRegion = null;
        file.epsgDescription = null;
        file.epsgError = message;
        this.cdr.detectChanges();
      }
    });
  }

  private extractApiFromFilename(filename: string): string | null {
    const matches = filename.match(this.api12DigitPattern);
    if (matches && matches.length > 0) {
      return matches[matches.length - 1];
    }
    return null;
  }

  private areAllFilesReadyForValidation(): boolean {
    return this.state.uploadedFiles.length > 0 && this.state.uploadedFiles.every(f => !f.needsMapping);
  }

  private resolveEpsgAndOpenDialog(): void {
    if (!this.areAllFilesReadyForValidation() || this.isUploading) {
      return;
    }

    const filenames = this.state.uploadedFiles.map(f => f.name);
    this.isUploading = true;
    this.uploadApi.resolveEpsg(filenames).subscribe({
      next: (response) => {
        const byFilename = new Map((response.results || []).map(r => [r.filename, r]));

        this.state.uploadedFiles.forEach((file) => {
          const resolved = byFilename.get(file.name);
          if (!resolved) {
            file.detectedEpsgCode = null;
            file.selectedEpsgCode = null;
            file.epsgRegion = null;
            file.epsgDescription = null;
            file.epsgError = 'Could not resolve EPSG for this file.';
            return;
          }

          file.api = resolved.api || file.api;
          file.detectedEpsgCode = resolved.epsg_code ?? null;
          file.epsgRegion = resolved.region ?? null;
          file.epsgDescription = resolved.region_description ?? null;
          file.epsgError = resolved.error || null;

          if (!file.selectedEpsgCode && resolved.epsg_code !== undefined && resolved.epsg_code !== null) {
            file.selectedEpsgCode = resolved.epsg_code;
          }
        });

        this.isUploading = false;
        this.showEpsgDialog = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        const message = err?.error?.detail || err?.message || 'Failed to resolve EPSG';
        this.state.uploadError = message;
        this.isUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  clearAll(): void {
    this.state.clearAll();
    this.epsgDialogShownOnce = false; // Reset flag when clearing all files
  }

  openResultsDialog(): void {
    this.showResultsDialog = true;
  }

  closeResultsDialog(): void {
    this.showResultsDialog = false;
  }

  sortByApi(): void {
    // Toggle sort direction: null -> asc -> desc -> null
    if (this.apiSortDirection === null) {
      this.apiSortDirection = 'asc';
    } else if (this.apiSortDirection === 'asc') {
      this.apiSortDirection = 'desc';
    } else {
      this.apiSortDirection = null;
    }

    if (this.apiSortDirection === null) {
      // No sort - could optionally restore original order
      // For now, we'll leave the current order
      return;
    }

    // Sort the uploadedFiles array by API
    this.state.uploadedFiles.sort((a, b) => {
      const apiA = a.api || '';
      const apiB = b.api || '';

      if (this.apiSortDirection === 'asc') {
        return apiA.localeCompare(apiB, undefined, { numeric: true });
      } else {
        return apiB.localeCompare(apiA, undefined, { numeric: true });
      }
    });

    this.cdr.detectChanges();
  }
}
